/**
 * Calculated MCF Extractors
 * 
 * For MCFs that derive their score from combining multiple question responses.
 * Each calculator takes a map of all user responses (keyed by question_key)
 * and returns a 0-100 score or null if inputs are missing.
 */

import type { McfExtractionResult } from './types';

// Midpoint approximations for range-based answers
const REVENUE_MIDPOINTS: Record<string, number> = {
  'under_250k': 125000,
  '250k_to_500k': 375000,
  '500k_to_1m': 750000,
  '1m_to_2m': 1500000,
  '2m_to_5m': 3500000,
  'over_5m': 5000000,
};

const STAFF_MIDPOINTS: Record<string, number> = {
  'solo': 1,
  '2_to_3': 2.5,
  '4_to_6': 5,
  '7_to_10': 8.5,
  '11_to_20': 15.5,
  'over_20': 25,
};

const CLIENT_MIDPOINTS: Record<string, number> = {
  'under_100': 50,
  '100_to_250': 175,
  '250_to_500': 375,
  '500_to_1000': 750,
  'over_1000': 1250,
};

/**
 * MCF3.4.2.1: Revenue per Staff Member
 * Formula: revenue_range ÷ team_size_total
 * Scored against industry benchmark tiers
 */
export function calculateRevenuePerStaff(
  allResponses: Record<string, any>
): number | null {
  const revenueAnswer = typeof allResponses['revenue_range'] === 'string'
    ? allResponses['revenue_range']
    : allResponses['revenue_range']?.value;
  const staffAnswer = typeof allResponses['team_size_total'] === 'string'
    ? allResponses['team_size_total']
    : allResponses['team_size_total']?.value;

  if (!revenueAnswer || !staffAnswer) return null;

  const revenue = REVENUE_MIDPOINTS[revenueAnswer];
  const staff = STAFF_MIDPOINTS[staffAnswer];

  if (revenue === undefined || staff === undefined || staff === 0) return null;

  const revenuePerStaff = revenue / staff;

  if (revenuePerStaff > 300000) return 100;
  if (revenuePerStaff >= 200000) return 75;
  if (revenuePerStaff >= 150000) return 55;
  if (revenuePerStaff >= 100000) return 35;
  return 15;
}

/**
 * MCF3.4.2.2: Client-to-Staff Ratio
 * Formula: client_count_range ÷ team_size_total
 * Scored against financial advisory industry capacity benchmarks
 */
export function calculateClientStaffRatio(
  allResponses: Record<string, any>
): number | null {
  const clientAnswer = typeof allResponses['client_count_range'] === 'string'
    ? allResponses['client_count_range']
    : allResponses['client_count_range']?.value;
  const staffAnswer = typeof allResponses['team_size_total'] === 'string'
    ? allResponses['team_size_total']
    : allResponses['team_size_total']?.value;

  if (!clientAnswer || !staffAnswer) return null;

  const clients = CLIENT_MIDPOINTS[clientAnswer];
  const staff = STAFF_MIDPOINTS[staffAnswer];

  if (clients === undefined || staff === undefined || staff === 0) return null;

  const clientsPerStaff = clients / staff;

  if (clientsPerStaff < 75) return 100;
  if (clientsPerStaff <= 150) return 70;
  if (clientsPerStaff <= 250) return 40;
  return 20;
}

// =====================================================
// Assessment 6 — Advisory Team Assembly MCFs
// =====================================================

/** The consolidated master gate question key */
const GATE_KEYS = [
  'advisor_team_members',
] as const;

/** The 6 core professional categories we look for across all gates */
const CORE_CATEGORIES = [
  'accountant_cpa', 'tax_specialist', 'corporate_lawyer',
  'estate_planning_attorney', 'succession_consultant', 'financial_planner',
] as const;

/** Score lookups for string option values (matching DB option scores) */
const SUCCESSION_EXPERIENCE_SCORES: Record<string, number> = {
  'extensive': 100, 'some': 65, 'limited': 35, 'none': 10,
};

const COORDINATION_SCORES: Record<string, number> = {
  // advisors_met_together
  'multiple_times': 100, 'once': 65, 'planned': 40, 'no_isolated': 10,
  // advice_coordinator
  'designated_coordinator': 100, 'i_coordinate': 70, 'i_manage_all': 40, 'no_coordination': 10,
};

const COMMUNICATION_SCORES: Record<string, number> = {
  // team_communication_frequency
  'regular_coordinated': 100, 'occasional_coordinated': 75, 'ad_hoc': 45, 'rare': 20,
  // info_sharing_permission
  'formal_consent': 100, 'verbal_permission': 70, 'selective': 40, 'no_sharing': 15,
};

const LAST_DISCUSSION_SCORES: Record<string, number> = {
  'within_6_months': 100, '6_12_months': 70, '1_2_years': 45, 'over_2_years': 25, 'never': 10,
};

/**
 * Helper: collect all selected advisors across all 4 gates.
 */
function getSelectedAdvisors(r: Record<string, any>): string[] {
  const selected: string[] = [];
  for (const gk of GATE_KEYS) {
    const val = r[gk];
    if (Array.isArray(val)) {
      selected.push(...val.filter((v: string) => v !== 'none_currently'));
    }
  }
  return [...new Set(selected)];
}

/**
 * MCF2.5.1.1: Team Composition Completeness
 * Score = (distinct core categories covered / 6) * 100
 */
export function calculateTeamComposition(r: Record<string, any>): number | null {
  const selected = getSelectedAdvisors(r);
  if (selected.length === 0) return 0;
  const covered = CORE_CATEGORIES.filter(c => selected.includes(c));
  return Math.round((covered.length / CORE_CATEGORIES.length) * 100);
}

/**
 * MCF2.5.1.2: Succession Experience Assessment
 * Best succession_experience score per gate category, then weighted average.
 */
export function calculateSuccessionExperience(r: Record<string, any>): number | null {
  const selected = getSelectedAdvisors(r);
  if (selected.length === 0) return null;

  const scores: number[] = [];
  for (const advisor of selected) {
    const key = `${advisor}_succession_experience`;
    const val = r[key];
    // Look up string option value → score
    const score = typeof val === 'string' ? SUCCESSION_EXPERIENCE_SCORES[val] : (typeof val === 'number' ? val : null);
    if (score !== undefined && score !== null) scores.push(score);
  }
  if (scores.length === 0) return null;

  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
}

/**
 * MCF2.5.2.1: Gap Identification
 * Inverse of how many gates have "none_currently" selected.
 */
export function calculateGapIdentification(r: Record<string, any>): number | null {
  let gapCount = 0;
  for (const gk of GATE_KEYS) {
    const val = r[gk];
    if (!val || (Array.isArray(val) && (val.length === 0 || val.includes('none_currently')))) {
      gapCount++;
    }
  }
  return Math.round(((GATE_KEYS.length - gapCount) / GATE_KEYS.length) * 100);
}

/**
 * MCF2.5.3.1: Coordination Approach
 * Average of advisors_met_together and advice_coordinator scores.
 */
export function calculateCoordinationApproach(r: Record<string, any>): number | null {
  const resolveScore = (val: any): number | null => {
    if (val === undefined || val === null) return null;
    if (typeof val === 'string') return COORDINATION_SCORES[val] ?? null;
    if (typeof val === 'number') return val;
    if (typeof val === 'object' && 'score' in val) return val.score;
    return null;
  };

  const s1 = resolveScore(r['advisors_met_together']);
  const s2 = resolveScore(r['advice_coordinator']);
  const vals = [s1, s2].filter((v): v is number => v !== null);
  if (vals.length === 0) return null;
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
}

/**
 * MCF2.5.3.2: Communication Protocol
 * Average of communication_frequency and info_sharing scores; binary threshold > 50.
 */
export function calculateCommunicationProtocol(r: Record<string, any>): number | null {
  const resolveScore = (val: any): number | null => {
    if (val === undefined || val === null) return null;
    if (typeof val === 'string') return COMMUNICATION_SCORES[val] ?? null;
    if (typeof val === 'number') return val;
    if (typeof val === 'object' && 'score' in val) return val.score;
    return null;
  };

  const s1 = resolveScore(r['team_communication_frequency']);
  const s2 = resolveScore(r['info_sharing_permission']);
  const vals = [s1, s2].filter((v): v is number => v !== null);
  if (vals.length === 0) return null;
  const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
  return avg > 50 ? 100 : 0;
}

/**
 * MCF2.5.4.1: Engagement Timeline
 * Average last_discussion score across all selected advisors (1-5 → 0-100).
 */
export function calculateEngagementTimeline(r: Record<string, any>): number | null {
  const selected = getSelectedAdvisors(r);
  if (selected.length === 0) return null;

  const scores: number[] = [];
  for (const advisor of selected) {
    const key = `${advisor}_last_discussion`;
    const val = r[key];
    // Look up string option value → score
    const score = typeof val === 'string' ? LAST_DISCUSSION_SCORES[val] : (typeof val === 'number' ? val : null);
    if (score !== undefined && score !== null) scores.push(score);
  }
  if (scores.length === 0) return null;

  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
}

// =====================================================
// Assessment 7 — Financial Readiness MCFs
// =====================================================

const NET_WORTH_SCORES: Record<string, number> = {
  under_250k: 35, '250k_500k': 50, '500k_1m': 65, '1m_2m': 75,
  '2m_5m': 85, over_5m: 90, prefer_not_to_say: 20,
};

const NET_WORTH_CURRENCY_SCORES: Record<string, number> = {
  within_90_days: 100, within_6_months: 80, within_1_year: 65,
  within_2_3_years: 40, over_3_years_or_never: 10,
};

/**
 * MCF1.4.1.2: Personal Net Worth Documentation
 * Composite: Q01 (net worth range) + Q01b (statement currency) + Q03 (real estate detail)
 */
export function calculatePersonalNetWorthDocumentation(r: Record<string, any>): number | null {
  const q01 = r['personal_net_worth_range'];
  const q01Score = NET_WORTH_SCORES[q01];
  if (q01Score === undefined) return null;

  const q01b = r['net_worth_statement_currency'];
  const q01bScore = q01 !== 'prefer_not_to_say' && q01b ? (NET_WORTH_CURRENCY_SCORES[q01b] ?? null) : null;

  const q03 = r['real_estate_portfolio'];
  let q03Score = 25;
  if (Array.isArray(q03)) {
    if (q03.includes('none_renting')) q03Score = 10;
    else if (q03.includes('investment_properties') && q03.includes('commercial_real_estate')) q03Score = 80;
    else if (q03.includes('investment_properties') || q03.includes('commercial_real_estate')) q03Score = 55;
  }

  const scores = [q01Score, q03Score];
  if (q01bScore !== null) scores.push(q01bScore);
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
}

/**
 * MCF1.4.2.1: Non-Practice Income Sources
 * Primary: count of income sources in Q09
 */
export function calculateNonPracticeIncomeSources(r: Record<string, any>): number | null {
  const q09 = r['post_retirement_income_sources'];
  if (!q09) return null;
  if (Array.isArray(q09)) {
    if (q09.includes('no_significant_income')) return 20;
    const count = q09.length;
    if (count >= 3) return 100;
    if (count === 2) return 75;
    if (count === 1) return 50;
    return 20;
  }
  if (q09 === 'no_significant_income') return 20;
  return 50;
}

/**
 * MCF1.4.4.1: Gap Analysis Completion
 * Composite: Q11 (runway) + Q12 (timeline) + Q13 (awareness) + Q14 (priorities)
 */
export function calculateGapAnalysisCompletion(r: Record<string, any>): number | null {
  const RUNWAY_SCORES: Record<string, number> = {
    under_5: 30, '5_10': 50, '10_15': 65, '15_20': 80, over_20: 100, cant_estimate: 10,
  };
  const TIMELINE_SCORES: Record<string, number> = {
    now: 100, within_2: 85, '3_5_years': 70, '5_10_years': 55, over_10_years: 40, not_sure: 10,
  };
  const AWARENESS_SCORES: Record<string, number> = {
    fully_aware: 100, good_understanding: 75, somewhat_aware: 50, not_very_aware: 30, shocked: 15,
  };

  const scores: number[] = [];

  const q11 = r['financial_runway_years'];
  if (q11 && RUNWAY_SCORES[q11] !== undefined) scores.push(RUNWAY_SCORES[q11]);

  const q12 = r['realistic_retirement_timeline'];
  if (q12 && TIMELINE_SCORES[q12] !== undefined) scores.push(TIMELINE_SCORES[q12]);

  const q13 = r['financial_gap_awareness'];
  if (q13 && AWARENESS_SCORES[q13] !== undefined) scores.push(AWARENESS_SCORES[q13]);

  const q14 = r['financial_readiness_priorities'];
  if (Array.isArray(q14)) {
    scores.push(q14.includes('financially_ready') ? 100 : q14.length > 0 ? 70 : 0);
  } else if (q14 === 'financially_ready') {
    scores.push(100);
  }

  if (scores.length === 0) return null;
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
}

/**
 * MCF1.1.2.2: Financial Number Clarity
 * Composite: Q01 (knowing net worth) + Q08 (income replacement engagement)
 */
export function calculateFinancialNumberClarity(r: Record<string, any>): number | null {
  const scores: number[] = [];

  const q01 = r['personal_net_worth_range'];
  if (q01) scores.push(q01 === 'prefer_not_to_say' ? 20 : 70);

  const q08 = r['income_replacement_percentage'];
  if (q08 !== undefined && q08 !== null) {
    scores.push(Number(q08) !== 70 ? 70 : 50);
  }

  if (scores.length === 0) return null;
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
}

/**
 * MCF1.3.2.1: Goal-Based Financial Calculation
 * Composite: Q06 (expenses known) + Q07 (expense change thought) + Q10 (obligations identified)
 */
export function calculateGoalBasedFinancialCalc(r: Record<string, any>): number | null {
  const EXPENSE_SCORES: Record<string, number> = {
    under_100k: 65, '100k_150k': 65, '150k_200k': 65, '200k_300k': 65,
    '300k_500k': 65, over_500k: 65, not_sure: 15,
  };
  const CHANGE_SCORES: Record<string, number> = {
    increase_significantly: 70, increase_somewhat: 65, stay_same: 60,
    decrease_somewhat: 70, decrease_significantly: 70, havent_thought: 15,
  };

  const scores: number[] = [];

  const q06 = r['annual_lifestyle_expenses'];
  if (q06 && EXPENSE_SCORES[q06] !== undefined) scores.push(EXPENSE_SCORES[q06]);

  const q07 = r['retirement_expense_expectation'];
  if (q07 && CHANGE_SCORES[q07] !== undefined) scores.push(CHANGE_SCORES[q07]);

  const q10 = r['ongoing_financial_obligations'];
  if (Array.isArray(q10)) {
    if (q10.includes('none_significant')) scores.push(85);
    else if (q10.length > 0) scores.push(70);
  }

  if (scores.length === 0) return null;
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
}

/**
 * MCF1.3.2.2: Total Financial Requirement Clarity
 * Composite: Q06 (expenses known) + Q08 (income replacement engagement)
 */
export function calculateTotalFinancialRequirementClarity(r: Record<string, any>): number | null {
  const scores: number[] = [];

  const q06 = r['annual_lifestyle_expenses'];
  if (q06) scores.push(q06 === 'not_sure' ? 15 : 65);

  const q08 = r['income_replacement_percentage'];
  if (q08 !== undefined && q08 !== null) {
    scores.push(Number(q08) !== 70 ? 70 : 50);
  }

  if (scores.length === 0) return null;
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
}

// =====================================================
// Assessment 8 — Successor Strategy MCFs
// =====================================================

/**
 * MCF2.1.3.2: Non-Financial Objectives Alignment
 * From Q01 ranking: how highly non-financial items rank
 */
export function calculateNonFinancialObjectivesAlignment(allResponses: Record<string, any>): number | null {
  const q01 = allResponses['succession_priorities_ranking'];
  if (!q01 || !Array.isArray(q01)) return null;

  const NON_FINANCIAL = ['preserve_culture', 'protect_jobs', 'maintain_clients', 'ensure_smooth', 'continue_legacy'];
  const totalItems = q01.length; // 8

  let nonFinRankSum = 0;
  let nonFinCount = 0;
  for (const item of q01) {
    if (NON_FINANCIAL.includes(item.value)) {
      nonFinRankSum += ((totalItems - item.rank + 1) / totalItems) * 100;
      nonFinCount++;
    }
  }
  if (nonFinCount === 0) return null;
  return Math.round(nonFinRankSum / nonFinCount);
}

/**
 * MCF1.3.3.2: Goal Prioritization for Trade-offs
 * From Q01 ranking: clarity of differentiation (spread between ranks)
 */
export function calculateGoalPrioritization(allResponses: Record<string, any>): number | null {
  const q01 = allResponses['succession_priorities_ranking'];
  if (!q01 || !Array.isArray(q01) || q01.length < 2) return null;

  return q01.length >= 8 ? 80 : 50;
}

/**
 * MCF2.3.1.1: Pathway Evaluation Status
 * Composite: Q02 Part A (primary) + Q02 Part B (fallback)
 */
export function calculatePathwayEvaluationStatus(allResponses: Record<string, any>): number | null {
  const primary = allResponses['exit_route_primary'];
  const fallback = allResponses['exit_route_fallback'];

  if (!primary) return null;

  if (primary === 'not_sure_yet') return fallback && fallback !== 'no_fallback' ? 35 : 25;

  if (fallback && fallback !== 'no_fallback' && fallback !== primary) return 100;
  if (!fallback || fallback === 'no_fallback') return 35;

  return 50;
}

/**
 * MCF2.1.2.2: Timeline-Strategy Consistency
 * Contextual: Q06 (flexibility) + Q02 (pathway type)
 */
export function calculateTimelineStrategyConsistency(allResponses: Record<string, any>): number | null {
  const FLEXIBILITY_SCORES: Record<string, number> = {
    must_12_months: 80, prefer_1_3_years: 85, flexible_3_5_years: 75,
    very_flexible: 50, no_timeline: 15,
  };
  const q06 = allResponses['succession_timeline_flexibility'];
  if (!q06) return null;

  const baseScore = FLEXIBILITY_SCORES[q06] ?? null;
  if (baseScore === null) return null;

  const primary = allResponses['exit_route_primary'];
  const SLOW_PATHWAYS = ['family_succession', 'gradual_buyout', 'associate_to_partner'];

  if (primary && SLOW_PATHWAYS.includes(primary) && q06 === 'must_12_months') {
    return Math.round(baseScore * 0.6);
  }

  return baseScore;
}

/**
 * MCF2.3.1.2: Evaluation Rigor
 * From Q04 Part A: count of deal breakers selected
 */
export function calculateEvaluationRigor(allResponses: Record<string, any>): number | null {
  const q04a = allResponses['succession_deal_breakers'];
  if (!q04a) return null;

  if (Array.isArray(q04a)) {
    const count = q04a.length;
    if (count >= 4) return 100;
    if (count >= 3) return 85;
    if (count >= 2) return 70;
    if (count >= 1) return 55;
    return 25;
  }
  return 25;
}

/**
 * MCF1.6.1.2: Family Capability Realism
 * Derived from Q07 Parts B + C: gap analysis
 */
export function calculateFamilyCapabilityRealism(allResponses: Record<string, any>): number | null {
  const INVOLVEMENT_RANK: Record<string, number> = {
    key_role_3plus: 5, key_role_under3: 4, junior_role: 3, occasional: 2, not_in_business: 1,
  };
  const READINESS_RANK: Record<string, number> = {
    ready_now: 5, near_ready: 4, developing: 3, early_stage: 2, not_sure: 1,
  };

  const partB = allResponses['family_business_involvement'];
  const partC = allResponses['family_readiness_to_lead'];

  if (!partB || !partC) return null;

  const involvementRank = INVOLVEMENT_RANK[partB];
  const readinessRank = READINESS_RANK[partC];
  if (involvementRank === undefined || readinessRank === undefined) return null;

  const gap = readinessRank - involvementRank;
  if (gap >= 3) return 20;
  if (gap >= 2) return 40;
  if (gap >= 1) return 60;
  if (gap === 0) return 85;
  return 100;
}

/**
 * MCF5.1.1.1: Pipeline Existence
 * Fed by Q07 Part B (family) OR Q08 Part A (internal) — whichever is relevant
 */
export function calculatePipelineExistence(allResponses: Record<string, any>): number | null {
  const FAMILY_SCORES: Record<string, number> = {
    key_role_3plus: 100, key_role_under3: 75, junior_role: 50, occasional: 25, not_in_business: 15,
  };
  const INTERNAL_SCORES: Record<string, number> = {
    proven_leaders: 100, strong_candidates: 75, potential: 45, no_candidates: 15,
  };

  const familyInvolvement = allResponses['family_business_involvement'];
  const internalReadiness = allResponses['internal_candidate_readiness'];

  const scores: number[] = [];
  if (familyInvolvement && FAMILY_SCORES[familyInvolvement] !== undefined) {
    scores.push(FAMILY_SCORES[familyInvolvement]);
  }
  if (internalReadiness && INTERNAL_SCORES[internalReadiness] !== undefined) {
    scores.push(INTERNAL_SCORES[internalReadiness]);
  }

  if (scores.length === 0) return null;
  return Math.max(...scores);
}

/**
 * MCF5.1.2.2: Successor Readiness Level
 * Fed by Q07 Part C (family) OR Q08 Part A (internal) — take best
 */
export function calculateSuccessorReadinessLevel(allResponses: Record<string, any>): number | null {
  const FAMILY_READINESS: Record<string, number> = {
    ready_now: 100, near_ready: 75, developing: 50, early_stage: 25, not_sure: 10,
  };
  const INTERNAL_READINESS: Record<string, number> = {
    proven_leaders: 100, strong_candidates: 75, potential: 45, no_candidates: 15,
  };

  const familyReadiness = allResponses['family_readiness_to_lead'];
  const internalReadiness = allResponses['internal_candidate_readiness'];

  const scores: number[] = [];
  if (familyReadiness && FAMILY_READINESS[familyReadiness] !== undefined) {
    scores.push(FAMILY_READINESS[familyReadiness]);
  }
  if (internalReadiness && INTERNAL_READINESS[internalReadiness] !== undefined) {
    scores.push(INTERNAL_READINESS[internalReadiness]);
  }

  if (scores.length === 0) return null;
  return Math.max(...scores);
}

/**
 * MCF2.1.3.1: Financial Objectives Alignment
 * From Q03: philosophy orientation
 */
export function calculateFinancialObjectivesAlignment(allResponses: Record<string, any>): number | null {
  const SCORES: Record<string, number> = {
    highest_price: 100, clean_exit: 70, gradual_income: 60,
    client_staff_care: 40, cultural_fit: 30, family_first: 25,
  };
  const q03 = allResponses['succession_philosophy'];
  if (!q03) return null;
  return SCORES[q03] ?? null;
}

// =====================================================
// Assessment 9 — Governance & Family Dynamics MCFs
// =====================================================

/**
 * MCF4.6.1.1: Governance Mechanism Count
 * From governance_mechanisms (multi_select): count non-"none" selections
 */
export function calculateGovernanceMechanismCount(r: Record<string, any>): number | null {
  const selected = r['governance_mechanisms'];
  if (!selected || !Array.isArray(selected)) return 10;

  const mechanisms = selected.filter((v: string) => v !== 'none');
  const count = mechanisms.length;

  if (count >= 5) return 100;
  if (count >= 3) return 80;
  if (count === 2) return 60;
  if (count === 1) return 40;
  return 10;
}

/**
 * MCF4.1.2.1: Buy-Sell Presence
 * From governance_mechanisms (multi_select): check if buy_sell_agreement is selected
 */
export function calculateBuySellPresence(r: Record<string, any>): number | null {
  const selected = r['governance_mechanisms'];
  if (!selected || !Array.isArray(selected)) return 10;

  return selected.includes('buy_sell_agreement') ? 65 : 10;
}

/**
 * MCF3.1.2.1: Decision Distribution Score
 * From decision_authority (single_select): measures distribution vs centralization
 */
export function calculateDecisionDistributionScore(r: Record<string, any>): number | null {
  const value = r['decision_authority'];
  const distributionMap: Record<string, number> = {
    'decide_alone': 20,
    'discuss_spouse': 35,
    'family_consensus': 60,
    'management_consensus': 80,
    'board_approval': 90,
    'formal_voting': 100,
  };
  return distributionMap[value] ?? 20;
}

/**
 * MCF4.6.3.2: Documentation Process Score
 * From corporate_documentation (single_select)
 */
export function calculateDocumentationProcessScore(r: Record<string, any>): number | null {
  const value = r['corporate_documentation'];
  const processMap: Record<string, number> = {
    'exist_not_sure': 10,
    'stored_computer': 25,
    'with_lawyer': 45,
    'accessible_reviewed': 75,
    'current_legal_reviewed': 100,
  };
  return processMap[value] ?? 10;
}

/**
 * MCF1.4.3.3: Estate Currency Score
 * From estate_succession_alignment (single_select)
 */
export function calculateEstateCurrencyScore(r: Record<string, any>): number | null {
  const value = r['estate_succession_alignment'];
  const currencyMap: Record<string, number> = {
    'fully_integrated': 100,
    'generally_aligned': 70,
    'exists_not_aligned': 45,
    'outdated': 15,
    'no_estate_plan': 0,
  };
  return currencyMap[value] ?? 0;
}

/**
 * MCF5.1.1.1 (A9 contribution): Children Pipeline Score
 * From children_business_interest (single_select)
 * Note: MCF5.1.1.1 is also fed by A8. A9 adds children-specific data.
 */
export function calculateChildrenPipelineScore(r: Record<string, any>): number | null {
  const value = r['children_business_interest'];
  const pipelineMap: Record<string, number> = {
    'highly_interested': 100,
    'interested_developing': 65,
    'somewhat_interested': 35,
    'not_interested': 0,
    'too_young': 10,
    'no_children': 0,
  };
  return pipelineMap[value] ?? 0;
}

// =====================================================
// Live Data MCFs — Advisory Team from team_members table
// =====================================================

import { supabase } from '@/integrations/supabase/client';

const FINANCIAL_TAX = ['accountant_cpa', 'tax_specialist', 'cra_specialist', 'financial_planner', 'investment_advisor'];
const LEGAL = ['corporate_business_lawyer', 'estate_planning_lawyer', 'tax_lawyer', 'securities_regulatory_lawyer', 'employment_lawyer', 'ip_lawyer', 'partnership_shareholder_specialist'];
const SUCCESSION = ['business_valuator_cbv', 'ma_advisor', 'succession_consultant', 'dealer_liaison', 'transition_coach'];
const SUPPLEMENTARY = ['insurance_advisor', 'wealth_manager', 'estate_trustee', 'risk_management_consultant', 'commercial_banker', 'business_banker', 'equipment_financing_specialist', 'sba_lender', 'hr_consultant', 'it_consultant', 'marketing_advisor', 'operations_consultant', 'bookkeeper'];

/**
 * MCF2.5.1.1: Professional Team Inventory (Live Data)
 * Scores breadth/depth of advisory team from team_members table.
 */
async function calculateMCF2511(effectiveTeamId: string): Promise<number> {
  if (!effectiveTeamId) return 0;

  const { data, error } = await supabase
    .from('team_members')
    .select('advisor_type, member_type')
    .eq('team_id', effectiveTeamId)
    .in('member_type', ['external', 'internal']);

  if (error || !data) return 0;

  // Filter: external = all, internal = core categories only
  const relevant = data.filter(m =>
    m.member_type === 'external' ||
    [...FINANCIAL_TAX, ...LEGAL, ...SUCCESSION].includes(m.advisor_type ?? '')
  );

  const types = relevant.map(m => m.advisor_type).filter(Boolean) as string[];

  // Coverage (70 points max)
  const hasFinancial = FINANCIAL_TAX.some(t => types.includes(t));
  const hasLegal = LEGAL.some(t => types.includes(t));
  const hasSuccession = SUCCESSION.some(t => types.includes(t));
  const hasSupplementary = SUPPLEMENTARY.some(t => types.includes(t));

  let score = 0;
  if (hasFinancial) score += 20;
  if (hasLegal) score += 20;
  if (hasSuccession) score += 20;
  if (hasSupplementary) score += 10;

  // Depth (20 points) — any single core category has 2+ members
  const financialCount = types.filter(t => FINANCIAL_TAX.includes(t)).length;
  const legalCount = types.filter(t => LEGAL.includes(t)).length;
  const successionCount = types.filter(t => SUCCESSION.includes(t)).length;
  if (financialCount >= 2 || legalCount >= 2 || successionCount >= 2) score += 20;

  // Core Accelerator (10 points) — all 3 core categories present
  if (hasFinancial && hasLegal && hasSuccession) score += 10;

  return Math.min(score, 100);
}

/**
 * MCF2.5.2.1: Gap Identification (Live Data)
 * Scores succession-specific advisor coverage from team_members table.
 */
async function calculateMCF2521(effectiveTeamId: string): Promise<number> {
  if (!effectiveTeamId) return 0;

  const { data, error } = await supabase
    .from('team_members')
    .select('advisor_type')
    .eq('team_id', effectiveTeamId)
    .in('advisor_type', SUCCESSION);

  if (error || !data) return 0;

  const count = new Set(data.map(m => m.advisor_type)).size;

  if (count >= 3) return 100;
  if (count === 2) return 70;
  if (count === 1) return 40;
  return 10;
}

/**
 * Router for all calculated MCFs — add new cases here as needed.
 * Async to support live-data MCFs that query the database.
 */
export async function extractCalculatedMcf(
  mcfCode: string,
  allResponses: Record<string, any>,
  effectiveTeamId?: string
): Promise<number | null> {
  switch (mcfCode) {
    case 'MCF3.4.2.1':
      return calculateRevenuePerStaff(allResponses);
    case 'MCF3.4.2.2':
      return calculateClientStaffRatio(allResponses);
    case 'MCF2.5.1.1':
      return await calculateMCF2511(effectiveTeamId || '');
    case 'MCF2.5.1.2':
      return calculateSuccessionExperience(allResponses);
    case 'MCF2.5.2.1':
      return await calculateMCF2521(effectiveTeamId || '');
    case 'MCF2.5.3.1':
      return calculateCoordinationApproach(allResponses);
    case 'MCF2.5.3.2':
      return calculateCommunicationProtocol(allResponses);
    case 'MCF2.5.4.1':
      return calculateEngagementTimeline(allResponses);
    // Assessment 7 — Financial Readiness
    case 'MCF1.4.1.2':
      return calculatePersonalNetWorthDocumentation(allResponses);
    case 'MCF1.4.2.1':
      return calculateNonPracticeIncomeSources(allResponses);
    case 'MCF1.4.4.1':
      return calculateGapAnalysisCompletion(allResponses);
    case 'MCF1.1.2.2':
      return calculateFinancialNumberClarity(allResponses);
    case 'MCF1.3.2.1':
      return calculateGoalBasedFinancialCalc(allResponses);
    case 'MCF1.3.2.2':
      return calculateTotalFinancialRequirementClarity(allResponses);
    // Assessment 8 — Successor Strategy
    case 'MCF2.1.3.2':
      return calculateNonFinancialObjectivesAlignment(allResponses);
    case 'MCF1.3.3.2':
      return calculateGoalPrioritization(allResponses);
    case 'MCF2.3.1.1':
      return calculatePathwayEvaluationStatus(allResponses);
    case 'MCF2.1.2.2':
      return calculateTimelineStrategyConsistency(allResponses);
    case 'MCF2.3.1.2':
      return calculateEvaluationRigor(allResponses);
    case 'MCF1.6.1.2':
      return calculateFamilyCapabilityRealism(allResponses);
    case 'MCF5.1.1.1': {
      // Best-of: A8 pipeline + A9 children pipeline
      const a8Score = calculatePipelineExistence(allResponses);
      const a9Score = calculateChildrenPipelineScore(allResponses);
      if (a8Score === null && a9Score === null) return null;
      return Math.max(a8Score ?? 0, a9Score ?? 0);
    }
    case 'MCF5.1.2.2':
      return calculateSuccessorReadinessLevel(allResponses);
    case 'MCF2.1.3.1':
      return calculateFinancialObjectivesAlignment(allResponses);
    // Assessment 9 — Governance & Family Dynamics
    case 'MCF4.6.1.1':
      return calculateGovernanceMechanismCount(allResponses);
    case 'MCF4.1.2.1':
      return calculateBuySellPresence(allResponses);
    case 'MCF3.1.2.1':
      return calculateDecisionDistributionScore(allResponses);
    case 'MCF4.6.3.2':
      return calculateDocumentationProcessScore(allResponses);
    case 'MCF1.4.3.3':
      return calculateEstateCurrencyScore(allResponses);
    default:
      console.warn(`[Scoring] No calculator for calculated MCF: ${mcfCode}`);
      return null;
  }
}

// =====================================================
// Live Data MCFs — Successor Candidates from successor_candidates table
// =====================================================

/**
 * MCF5.1.1.1: Pipeline Existence (Live Data)
 * Reads from successor_candidates to score pipeline maturity.
 */
async function calculateMCF5111(userId: string): Promise<number> {
  const { data: candidates, error } = await supabase
    .from('successor_candidates')
    .select('id, successor_role, status')
    .eq('user_id', userId);

  if (error) {
    console.error('[calculateMCF5111] query failed:', error.message);
    throw new Error(error.message);
  }

  const planned = candidates?.filter(c => c.successor_role !== 'contingent') ?? [];
  const contingentOnly = candidates?.filter(c => c.successor_role === 'contingent') ?? [];
  const selectedPlanned = planned.filter(c => c.status === 'selected');

  const planExists = false; // Wire to A11 output when Plan module is complete
  if (selectedPlanned.length > 0 && planExists) return 100;
  if (selectedPlanned.length > 0) return 70;
  if (planned.length > 0) return 40;
  if (contingentOnly.length > 0 && planned.length === 0) return 20;
  return 0;
}

/**
 * MCF5.3.3.1: Backup Candidate Identification (Live Data)
 * Reads from successor_candidates to score contingent coverage.
 */
async function calculateMCF5331(userId: string): Promise<number> {
  const { data: candidates, error } = await supabase
    .from('successor_candidates')
    .select('id, successor_role, status, readiness_level')
    .eq('user_id', userId);

  if (error) {
    console.error('[calculateMCF5331] query failed:', error.message);
    throw new Error(error.message);
  }

  const contingent = candidates?.filter(c => c.successor_role === 'contingent') ?? [];
  const selectedContingent = contingent.filter(c => c.status === 'selected');

  const planExists = false; // Wire to A11 output when Plan module is complete
  if (selectedContingent.length > 0 && planExists) return 100;
  if (selectedContingent.length > 0) return 70;
  if (contingent.length > 0) return 40;
  return 0;
}

/**
 * Live-data MCF pipeline — runs independently of question-matching.
 * Returns McfExtractionResult[] for MCFs that pull from team_members and successor_candidates.
 */
const LIVE_MCF_IDS: Record<string, string> = {
  'MCF2.5.1.1': 'b0b2e493-d580-4e7f-81c6-39be3a78dc83',
  'MCF2.5.2.1': 'f0043b91-f041-4f4b-930d-a571a475a16e',
  'MCF5.1.1.1': '937c7570-4539-420f-93db-d02e194e44ac',
  'MCF5.3.3.1': 'b87fbe13-be18-41a2-8369-da1602dbe9fa',
};

export async function calculateLiveDataMcfs(
  userId: string,
  effectiveTeamId: string
): Promise<McfExtractionResult[]> {
  const [score2511, score2521, score5111, score5331] = await Promise.all([
    calculateMCF2511(effectiveTeamId),
    calculateMCF2521(effectiveTeamId),
    calculateMCF5111(userId),
    calculateMCF5331(userId),
  ]);

  return [
    {
      mcfId: LIVE_MCF_IDS['MCF2.5.1.1'],
      mcfCode: 'MCF2.5.1.1',
      score: score2511,
      rawAnswer: { type: 'live_data', source: 'team_members' },
      calculationMethod: 'live_data',
      sourceQuestionKey: '',
    },
    {
      mcfId: LIVE_MCF_IDS['MCF2.5.2.1'],
      mcfCode: 'MCF2.5.2.1',
      score: score2521,
      rawAnswer: { type: 'live_data', source: 'team_members' },
      calculationMethod: 'live_data',
      sourceQuestionKey: '',
    },
    {
      mcfId: LIVE_MCF_IDS['MCF5.1.1.1'],
      mcfCode: 'MCF5.1.1.1',
      score: score5111,
      rawAnswer: { type: 'live_data', source: 'successor_candidates' },
      calculationMethod: 'live_data',
      sourceQuestionKey: '',
    },
    {
      mcfId: LIVE_MCF_IDS['MCF5.3.3.1'],
      mcfCode: 'MCF5.3.3.1',
      score: score5331,
      rawAnswer: { type: 'live_data', source: 'successor_candidates' },
      calculationMethod: 'live_data',
      sourceQuestionKey: '',
    },
  ];
}
